import { Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Stale build chunks after a new deploy: reload once to fetch the new build.
    const message = error?.message ?? '';
    const isChunkError =
      /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i.test(
        message,
      );
    if (isChunkError) {
      const last = Number(sessionStorage.getItem('chunk-reload-at') ?? 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem('chunk-reload-at', String(Date.now()));
        window.location.reload();
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-6">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="font-display text-2xl mb-4">เกิดข้อผิดพลาด</h1>
            <p className="text-muted-foreground mb-6">
              ขออภัย เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง
            </p>
            {this.state.error && (
              <p className="text-xs text-muted-foreground/70 mb-6 font-mono bg-muted p-3 rounded-lg overflow-auto max-h-32">
                {this.state.error.message}
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRetry} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                ลองใหม่
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/'}>
                กลับหน้าแรก
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
